import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Pencil } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { fetchAdminProvider, type AdminProviderDetail } from "../api/adminApi";
import { getAuthToken } from "../auth";

type ProviderDetailPageProps = {
  providerId?: number;
};

function display(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  return v || "—";
}

function parseId(raw: string | undefined): number {
  if (!raw) return NaN;
  const n = Number(raw.trim().replace(/^prov-/i, ""));
  return Number.isFinite(n) ? n : NaN;
}

export function ProviderDetailPage({ providerId: providerIdProp }: ProviderDetailPageProps) {
  const params = useParams();
  const providerId = providerIdProp && providerIdProp > 0 ? providerIdProp : parseId(params.id);
  const [provider, setProvider] = useState<AdminProviderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!providerId || Number.isNaN(providerId)) {
      setError("Identifiant invalide.");
      setLoading(false);
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise pour afficher la fiche.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const row = await fetchAdminProvider(providerId);
        if (cancelled) return;
        if (!row || !row.id) {
          setError("Prestataire introuvable.");
          setProvider(null);
          return;
        }
        setProvider(row);
      } catch (err) {
        if (cancelled) return;
        setProvider(null);
        setError(err instanceof Error ? err.message : "Prestataire introuvable.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [providerId]);

  if (loading) {
    return (
      <div className="theme-card p-8 text-center text-sm text-muted-foreground">
        Chargement du prestataire…
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Prestataire introuvable."}</p>
        <Link
          to="/dashboard/providers"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={14} />
          Retour à la liste
        </Link>
      </div>
    );
  }

  const isActive = provider.status === "active";
  const title = display(provider.commercial_name);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref="/dashboard/admin"
          items={[
            { label: "Gestion" },
            { label: "Prestataires de Soins", href: "/dashboard/providers" },
            { label: title },
          ]}
        />
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/providers"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <ArrowLeft size={15} />
            Retour
          </Link>
          <Link
            to={`/dashboard/providers/${provider.id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
          >
            <Pencil size={14} />
            Modifier
          </Link>
        </div>
      </div>

      <div className="theme-card p-6">
        <div className="mb-6 flex flex-wrap items-start gap-4">
          <span className="inline-flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Building2 size={28} />
          </span>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <StatusBadge variant="purple">
                {provider.type || display(provider.type_raw)}
              </StatusBadge>
              <StatusBadge variant={isActive ? "success" : "neutral"}>
                {isActive ? "ACTIF" : "INACTIF"}
              </StatusBadge>
              {provider.agreement_number ? (
                <StatusBadge variant="info">Agrément {provider.agreement_number}</StatusBadge>
              ) : null}
            </div>
          </div>
        </div>

        {provider.description ? (
          <p className="mb-6 whitespace-pre-wrap text-sm text-muted-foreground">{provider.description}</p>
        ) : null}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Localisation
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Adresse
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-foreground">{display(provider.address)}</dd>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Ville
                  </dt>
                  <dd className="mt-1 text-foreground">{display(provider.city)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Province
                  </dt>
                  <dd className="mt-1 text-foreground">{display(provider.province)}</dd>
                </div>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Code postal
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.postal_code)}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Contact
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Téléphone
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.phone)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Email
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.email)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Site web
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.website)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Personne de contact
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.contact_name)}</dd>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Tél. contact
                  </dt>
                  <dd className="mt-1 text-foreground">{display(provider.contact_phone)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Email contact
                  </dt>
                  <dd className="mt-1 text-foreground">{display(provider.contact_email)}</dd>
                </div>
              </div>
            </dl>
          </section>

          <section className="md:col-span-2">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Identifiants & spécialités
            </h2>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  RCCM
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.rccm)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  ID NAT
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.id_nat)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  N° agrément
                </dt>
                <dd className="mt-1 text-foreground">{display(provider.agreement_number)}</dd>
              </div>
              <div className="sm:col-span-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Spécialités
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-foreground">
                  {display(provider.specialties)}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </>
  );
}
